import { BloodTest, BloodTestAnalyte, BloodTestResult } from "../../types/lab/BloodTestTypes";
import { Test } from "../../types/healthcare/TestTypes";

/**
 * Pure shaping helpers for blood analytes.
 *
 * ⚠️ Deliberately NOT in `api/healthcare/Testing/BloodTestApi.ts`: that module reads
 * `import.meta.env` at load, which ts-jest cannot parse, so importing a pure helper from
 * there forced every consumer's test to stub `runtimeEnv`. Nothing here touches HTTP.
 */


/**
 * Parse a lab date without converting it.
 *
 * ⚠️ `blood_tests.collected_at` / `reported_at` are `timestamp WITHOUT time zone` holding the
 * moment the LAB printed, but Laravel serialises them with a `Z`. `new Date(...)` then reads
 * lab-local wall clock as UTC and a 23:45 collection lands on the next day east of UTC.
 */
export const labWallClockDate = (value?: string | Date | null): Date | null => {
	if (!value) return null;
	if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
	const raw = String(value).trim();
	const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/);
	if (!m) {
		const loose = new Date(raw);
		return isNaN(loose.getTime()) ? null : loose;
	}
	return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4] ?? 0), Number(m[5] ?? 0), Number(m[6] ?? 0));
};

/**
 * Identity of an analyte ACROSS orders.
 *
 * ⚠️ Neither obvious key works alone. `test_category` is the per-order TestResult BUCKET, and
 * an unresolved analyte falls back into an ordered type — so `Icterus` sits under
 * `total_cholesterol` and would be plotted on a cholesterol trend. `test_name` is what the lab
 * printed, and one analyte arrives as `Hct` and `Haematocrit` on different reports, splitting
 * its history. `canonical_type` is the backend's per-analyte resolve (null when unresolved),
 * which is both stable across spellings and absent for the contaminants.
 */
export const analyteIdentity = (result?: { canonical_type?: string | null; test_name?: string | null } | null): string => {
	const canonical = (result?.canonical_type ?? "").trim().toLowerCase();
	if (canonical) return `type:${canonical}`;
	return `name:${(result?.test_name ?? "").trim().toLowerCase()}`;
};

// Pre-S7 rows still carry the old keys until the operator rename lands; S14 (86d439tn0) removes this shim
type LegacyAnalyte = BloodTestAnalyte & { reference_range?: string | null; reference_min?: number | null; reference_max?: number | null; flag?: string | null };

export const withLabRangeKeys = <T extends LegacyAnalyte>(analyte: T): T => ({
	...analyte,
	lab_reference_range: analyte.lab_reference_range ?? analyte.reference_range ?? null,
	lab_reference_min: analyte.lab_reference_min ?? analyte.reference_min ?? null,
	lab_reference_max: analyte.lab_reference_max ?? analyte.reference_max ?? null,
	lab_flag: analyte.lab_flag ?? analyte.flag ?? null,
});

/**
 * Flatten the `test.test_results[].results` structure into the flat `BloodTest.results`
 * array consumed by the blood wheel and analysis components. Each TestResult row groups its
 * analytes under one ordered test_type; that parent type becomes the analyte
 * `test_category` and a stable synthetic `id` is assigned for keying/selection.
 */
export const flattenBloodTestResults = (bloodTest: BloodTest): BloodTest => {
	if (!bloodTest) return bloodTest;

	const testResults = bloodTest.test?.test_results ?? [];
	const flattened: BloodTestResult[] = [];
	let syntheticId = 1;

	testResults.forEach((testResult) => {
		// Defensive: a small number of legacy/malformed rows carry a non-array value in
		// `results` (e.g. a raw file-descriptor string from an older upload path bug).
		const results = Array.isArray(testResult.results) ? testResult.results : [];
		results.forEach((analyte) => {
			flattened.push({
				...withLabRangeKeys(analyte),
				id: syntheticId++,
				blood_test_id: bloodTest.id,
				test_category: testResult.test_type ?? null,
				// ⚠️ NOT `results_available_date` — that is the IMPORT timestamp
				// (`ImportBloodTestsFromLabiq` hardcoded `now()`), identical across orders.
				collected_at: bloodTest.collected_at ?? null,
				reported_at: bloodTest.reported_at ?? testResult.results_available_date ?? null,
			});
		});
	});

	return { ...bloodTest, results: flattened };
};

/**
 * Build a flattened BloodTest from a TEST ORDER.
 *
 * ⚠️ The analytes live on the ORDER (`test_results[].results`), never under
 * `bloodTest.test.test_results` — `TestController` eager-loads `bloodTest.analysis` and not
 * the circular `bloodTest.test.testResults`, so flattening the blood test alone yields an
 * empty set and silently loses every historical point.
 */
export const bloodTestFromOrder = (order?: Partial<Test> | null): BloodTest | null => {
	if (!order) return null;

	const header: any = order.bloodTest ?? (order as any).blood_test ?? null;
	const hasAnalytes = (order.test_results ?? []).some((tr: any) => Array.isArray(tr?.results) && tr.results.length > 0);
	if (!header && !hasAnalytes) return null;

	return flattenBloodTestResults({
		...(header ?? {}),
		id: header?.id ?? (order as any).blood_test_id ?? 0,
		collected_at: header?.collected_at ?? null,
		reported_at: header?.reported_at ?? order.test_date ?? null,
		analysis: header?.analysis ?? null,
		test: order as any,
	} as BloodTest);
};
